/**
 * Shared geometry for activity→activity relationship links (WI #5531).
 *
 * The bezier math + "which links are drawn" rules live here so three consumers
 * stay in sync: `LinksPainter` (draws them), the interaction router (click →
 * select / delete-button hit), and `LinkDeleteButtonPainter` (the trash button on
 * the selected link). All coordinates are screen space, identical to
 * `scene.screenX` (START_X − offset + scaleX), so painter and hit-test never
 * drift — the geometry matches MapArrow.vue / MapActivityLinkDeleteButton.vue of
 * the retired SVG renderer (deleted in WI #5609).
 */
import { START_X } from '@/store/remapStore';
import { activityLayout, markerY } from '../../layout/activityLayout';
import type { IoStore, MapStore } from '../../engine/SceneContext';
import type { MapActivity } from '@/store/ioStore';

export interface LinkEndpoints {
  ax: number;
  ay: number;
  bx: number;
  by: number;
}

/** Click tolerance to the bezier (SVG hit path is a 10px stroke → 5px each side). */
const HIT_TOLERANCE = 6;
/** Trash-button radius on the selected link (SVG button is a 32px circle). */
export const LINK_DELETE_RADIUS = 16;

function screenX(map: MapStore, pos: number): number {
  return START_X - map.offset + map.scaleX(pos);
}

/**
 * Screen point a link endpoint anchors to: the collapsed group MARKER when
 * the activity is hidden behind one (matching LinksPainter via
 * `layoutAnchor` — the interaction side reads the live engine directly),
 * else the activity's own position with the layout's animated Y.
 */
function linkAnchor(map: MapStore, a: MapActivity): { x: number; y: number } {
  const group = activityLayout.markerOf(a.id);
  if (group) {
    return {
      x: screenX(map, group.pos),
      y: markerY(group, (id) => activityLayout.yOf(id, map)),
    };
  }

  return {
    x: screenX(map, map.activityPos(a)),
    y: activityLayout.yOf(a.id, map),
  };
}

/** Bezier endpoints for a link (MapArrow: end x is pulled back 22px for the head). */
export function linkEndpoints(
  map: MapStore,
  a: MapActivity,
  b: MapActivity,
): LinkEndpoints {
  // Anchors come from the live activity layout (collapsed-marker
  // substitution, animated cluster-fan position, store fallback), so the
  // click hit-path and the delete-button anchor stay glued to the drawn link.
  const pa = linkAnchor(map, a);
  const pb = linkAnchor(map, b);

  return { ax: pa.x, ay: pa.y, bx: pb.x - 22, by: pb.y };
}

/** Point at parameter `t` on the link's cubic bezier (control pts +30 / −80 in x). */
export function bezierPointAt(
  e: LinkEndpoints,
  t: number,
): { x: number; y: number } {
  const mt = 1 - t;
  const p1x = e.ax + 30;
  const p2x = e.bx - 80;

  return {
    x:
      mt * mt * mt * e.ax +
      3 * mt * mt * t * p1x +
      3 * mt * t * t * p2x +
      t * t * t * e.bx,
    y:
      mt * mt * mt * e.ay +
      3 * mt * mt * t * e.ay +
      3 * mt * t * t * e.by +
      t * t * t * e.by,
  };
}

/**
 * Directed link keys (`pred-succ`) that are drawn — hence clickable — for the
 * current linkMode + selection + hover; `null` means "all links" (linkMode
 * 'all'). The currently selected link is always included so it stays visible +
 * highlighted regardless of mode. Ported from MapActivityLinks' link lists.
 */
export function activeLinkKeys(
  map: MapStore,
  byId: Map<string, MapActivity>,
): Set<string> | null {
  if (map.linkMode === 'all') {
    return null;
  }
  const ids = new Set<string>();
  // Keep the selected link drawn/clickable even in single / none modes.
  if (
    map.selectedContext === 'link' &&
    map.selectedLink[0] &&
    map.selectedLink[1]
  ) {
    ids.add(`${map.selectedLink[0]}-${map.selectedLink[1]}`);
  }
  if (map.linkMode === 'none') {
    return ids;
  }
  const multi = map.linkMode === 'multi';

  const addFor = (activityId: string): void => {
    const act = byId.get(activityId);
    if (!act) {
      return;
    }
    for (const p of act.predecessors) {
      ids.add(`${p}-${activityId}`);
    }
    for (const s of act.successors) {
      ids.add(`${activityId}-${s}`);
    }
    if (!multi) {
      return;
    }
    for (const p of act.predecessors) {
      byId.get(p)?.predecessors.forEach((pp) => ids.add(`${pp}-${p}`));
    }
    for (const s of act.successors) {
      byId.get(s)?.successors.forEach((ss) => ids.add(`${s}-${ss}`));
    }
  };

  const selectionValid =
    (!!map.selectedId || map.selectedIds.length > 0) &&
    (map.selectedContext === 'activity' ||
      map.selectedContext === 'link' ||
      map.selectedContext === 'mirroredActivity');
  if (selectionValid) {
    if (map.selectedIds.length === 0) {
      addFor(map.selectedId);
    } else {
      map.selectedIds.forEach(addFor);
    }
  }
  if (map.hoveredActivity) {
    addFor(map.hoveredActivity);
  }
  // Hovering a collapsed group marker shows its members' links exactly as if
  // each member were hovered itself — the marker is their visible stand-in
  // (marker hover suppresses activity hover, so hoveredActivity can't cover
  // this). Same single/multi fan-out rules per member via addFor.
  const hoveredGroupKey = activityLayout.hoveredGroup.value;
  if (hoveredGroupKey) {
    const group = activityLayout
      .collapsedGroups()
      .find((g) => g.key === hoveredGroupKey);
    group?.memberIds.forEach(addFor);
  }

  return ids;
}

/** The drawn link whose bezier is closest to the point within tolerance, or null. */
export function linkAt(
  map: MapStore,
  io: IoStore,
  px: number,
  py: number,
): [string, string] | null {
  const all = io.activities;
  const byId = new Map(all.map((a) => [a.id, a]));
  const active = activeLinkKeys(map, byId);
  if (active && active.size === 0) {
    return null;
  }

  let best: [string, string] | null = null;
  let bestDist = HIT_TOLERANCE;
  for (const a of all) {
    for (const successorId of a.successors) {
      const b = byId.get(successorId);
      if (!b) {
        continue;
      }
      if (active && !active.has(`${a.id}-${successorId}`)) {
        continue;
      }
      // Same-marker links aren't drawn (LinksPainter) → not clickable either.
      const ga = activityLayout.markerOf(a.id);
      if (ga && ga === activityLayout.markerOf(successorId)) {
        continue;
      }
      const e = linkEndpoints(map, a, b);
      // Bounding-box quick reject (control points push ±30 in x past the ends).
      if (
        px < Math.min(e.ax, e.bx) - 30 - HIT_TOLERANCE ||
        px > Math.max(e.ax, e.bx) + 30 + HIT_TOLERANCE ||
        py < Math.min(e.ay, e.by) - HIT_TOLERANCE ||
        py > Math.max(e.ay, e.by) + HIT_TOLERANCE
      ) {
        continue;
      }
      const d = distanceToBezier(e, px, py);
      if (d < bestDist) {
        bestDist = d;
        best = [a.id, b.id];
      }
    }
  }

  return best;
}

/** Min distance from the point to the link's bezier (sampled into segments). */
function distanceToBezier(e: LinkEndpoints, px: number, py: number): number {
  const SAMPLES = 18;
  let min = Infinity;
  let prev = bezierPointAt(e, 0);
  for (let i = 1; i <= SAMPLES; i++) {
    const cur = bezierPointAt(e, i / SAMPLES);
    const d = distanceToSegment(px, py, prev.x, prev.y, cur.x, cur.y);
    if (d < min) {
      min = d;
    }
    prev = cur;
  }

  return min;
}

function distanceToSegment(
  px: number,
  py: number,
  x1: number,
  y1: number,
  x2: number,
  y2: number,
): number {
  const dx = x2 - x1;
  const dy = y2 - y1;
  const lenSq = dx * dx + dy * dy;
  const t = lenSq
    ? Math.max(0, Math.min(1, ((px - x1) * dx + (py - y1) * dy) / lenSq))
    : 0;
  const cx = x1 + t * dx;
  const cy = y1 + t * dy;

  return Math.hypot(px - cx, py - cy);
}

/** Centre of the trash button on the selected link (t=0.55 on the bezier), or null. */
export function selectedLinkDeletePoint(
  map: MapStore,
  io: IoStore,
): { x: number; y: number } | null {
  if (map.selectedContext !== 'link') {
    return null;
  }
  const [predId, succId] = map.selectedLink;
  if (!predId || !succId) {
    return null;
  }
  const a = io.activity(predId);
  const b = io.activity(succId);
  if (!a || !b) {
    return null;
  }

  return bezierPointAt(linkEndpoints(map, a, b), 0.55);
}

/** Whether the point is on the selected link's trash button. */
export function linkDeleteButtonAt(
  map: MapStore,
  io: IoStore,
  px: number,
  py: number,
): boolean {
  const p = selectedLinkDeletePoint(map, io);
  if (!p) {
    return false;
  }
  const dx = px - p.x;
  const dy = py - p.y;

  return dx * dx + dy * dy <= LINK_DELETE_RADIUS * LINK_DELETE_RADIUS;
}
